<template>
  <div class="source-breakdown-chart">
    <div v-if="loading" class="text-center py-5">
      <a-spin size="large" />
    </div>
    <div v-else>
      <div class="chart-header">
        <h6 class="chart-title">{{ $t("financialStats.revenueSourcesTitle") }}</h6>
        <div class="chart-subtitle">{{ $t("financialStats.revenueSourcesSubtitle") }}</div>
      </div>

      <apexchart
        v-if="chartData.series && chartData.series.length"
        :options="chartOptions"
        :series="chartData.series"
        type="donut"
        height="320"
        class="chart-container"
      />
      <div v-else class="text-muted text-center py-4">{{ $t("financialStats.noData") }}</div>

      <div class="chart-legend mt-3">
        <div v-for="(item, index) in data" :key="index" class="legend-item">
          <div class="legend-color" :style="{ backgroundColor: getColor(index) }"></div>
          <span class="legend-label">{{ getSourceLabel(item && item.name) }}</span>
          <span class="legend-value">{{ item && item.value != null ? Number(item.value).toFixed(1) : 0 }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  name: "SourceBreakdownChart",
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Array,
      default: () => [],
    },
    totalAmount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      currency: null,
    };
  },
  created() {
    this.currency = getBuildingCurrency();
  },
  computed: {
    chartData() {
      if (!this.data || this.data.length === 0) {
        return { series: [] };
      }
      return {
        series: this.data.map((item) => {
          const v = item && item.value !== undefined && item.value !== null ? Number(item.value) : 0;
          return Math.round(v * 100) / 100;
        }),
      };
    },
    chartOptions() {
      return {
        chart: {
          type: "donut",
          height: 320,
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800,
          },
        },
        colors: ["#52c41a", "#1890ff", "#722ed1"],
        labels: this.data.map((item) => this.getSourceLabel(item && item.name)),
        dataLabels: {
          enabled: true,
          formatter: (val) => Number(val).toFixed(1) + "%",
          style: {
            fontSize: "12px",
            fontWeight: "bold",
            colors: ["#fff"],
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "60%",
              labels: {
                show: true,
                total: {
                  show: true,
                  showAlways: true,
                  label: this.$t("financialStats.total"),
                  formatter: () => {
                    if (!this.totalAmount) return `0 ${this.currency}`;
                    return Number(this.totalAmount).toLocaleString() + ` ${this.currency}`;
                  },
                },
              },
            },
          },
        },
        legend: {
          show: false,
        },
        tooltip: {
          y: {
            formatter: (value, { seriesIndex }) => {
              const item = this.data[seriesIndex];
              if (item && this.totalAmount) {
                const amount = (this.totalAmount * item.value) / 100;
                return Number(amount).toLocaleString() + ` ${this.currency}`;
              }
              return value ? Number(value).toLocaleString() + ` ${this.currency}` : `0 ${this.currency}`;
            },
          },
        },
      };
    },
  },
  methods: {
    getColor(index) {
      const colors = ["#52c41a", "#1890ff", "#722ed1"];
      return colors[index % colors.length];
    },
    getSourceLabel(name) {
      if (!name) return "—";
      return this.$t("financialStats.revenueSource." + name);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/shared.scss";
</style>
